import * as React from 'react';
import { Formik, FormikProps } from 'formik';
import { getPromise } from 'fingerprintjs2';
import { useNavigate, useLocation } from 'react-router-dom';
import DocumentField from './dataCapture/DocumentField';
import PrimaryButton from './buttons/PrimaryButton';
import SecondaryButton from './buttons/SecondaryButton';
import { MiscDocumentTypes } from '../../core/enums';
import Reference from '../../models/be/Reference';
import { validate } from './dataCapture/form';
import { Component } from '../../models/be/db/FormSpecification';
import { hasValue } from '../../lib/utils';
import Document from '../../models/be/db/Document';
import ErrorMessage from '../../commonComponents/ErrorMessage';
import Loading from '../../commonComponents/Loading';
import { completeReferenceAsReferee, setRefereeConsent } from '../../core/api';
import { clearRefereeTokenInLocalStorage } from '../../core/session';
import './ReferenceReplacementDocument.less';
import { Session } from '../../models/be/transport/Session';
import { SaveAndFinishLater } from '../pages/refereeSaved/RefereeSaved';
import { useAsyncAction } from '../../commonComponents/Hooks';
import MessageBox from './MessageBox';
import Buttons from './Buttons';
import Workflow from '../../models/be/Workflow';

const ReferenceReplacementDocument = (props: Props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const formikInitialized = React.useRef(false);
	const [loading, errorMessage, performAsyncAction] = useAsyncAction(false);
	const [session, setSession] = React.useState<Session>(null);
	const { showSaveAndFinish } = props;

	React.useEffect(() => {
		(async () => {
			// getPromise returns a full browser fingerprint based on the users browser, we obfuscate it by encoding it as base64 before transporting it to the API
			setSession({ item1: btoa(JSON.stringify(await getPromise())) });
		})();
	}, []);

	const handlePrimaryButtonClick = (formikProps: FormikProps<FormikValues>) => async () => {
		// We must submit the form when it isn't valid to let formik to set all fields to touched to show errors,
		// even though the submit handler does nothing.
		await performAsyncAction(async () => {
			await formikProps.submitForm();
			if (!hasValue(formikProps.values.childReference.referenceReplacementDocument?.description)) {
				throw new Error('Description is required.');
			}

			if (!hasValue(formikProps.values.childReference.referenceReplacementDocument?.blob)) {
				throw new Error('Please upload a valid document.');
			}

			if (formikProps.isValid) {
				await setRefereeConsent(props.refereeToken, true);
				await completeReferenceAsReferee(props.refereeToken, false, session, false);
				clearRefereeTokenInLocalStorage();
				if (props.onSubmitButtonClick) {
					props.onSubmitButtonClick();
				} else {
					navigate('/referee/thanks');
				}
			}
		});
		return false;
	};

	const initialValues: FormikValues = {
		childReference: {
			referenceReplacementDocument: props.reference.referenceReplacementDocument
		}
	};

	const components: Component[] = [
		{
			id: null,
			name: null,
			fieldNames: ['childReference.referenceReplacementDocument'],
			props: {
				documentType: MiscDocumentTypes.ReferenceReplacementDocument,
				type: 'file',
				required: true
			}
		}
	];

	return (
		<form
			className="referenceReplacementDocument"
			onSubmit={(e) => {
				e.preventDefault();
			}}
		>
			<MessageBox error={errorMessage} />
			<Formik initialValues={initialValues} validate={validate(components)} onSubmit={() => {}}>
				{(formikProps: FormikProps<Workflow>) => {
					if (loading) {
						return <Loading />;
					}

					if (!formikInitialized.current) {
						// This forces Formik to run the validation code when it mounts for each new page.
						formikProps.submitForm();
						formikInitialized.current = true;
					}

					const document = formikProps.values.childReference?.referenceReplacementDocument ?? null;
					return (
						<>
							<h3>REFERENCE REPLACEMENT DOCUMENT</h3>
							<section className="borderContent">
								<DocumentField
									title=""
									fileDescriptionLabel="File Description"
									documentTypes={[MiscDocumentTypes.ReferenceReplacementDocument]}
									fieldNames={['childReference.referenceReplacementDocument']}
									customerId={props.customerId}
									refereeToken={props.refereeToken}
								/>
								{hasValue(formikProps.errors.childReference) && (
									<ErrorMessage
										message={formikProps.errors.childReference?.referenceReplacementDocument}
										absolute={false}
									/>
								)}
							</section>
							<Buttons>
								<SecondaryButton text="Back" onClick={() => props.onBackButtonClick(document)} />
								<PrimaryButton text="Submit" onClick={handlePrimaryButtonClick(formikProps)} />
							</Buttons>
							{showSaveAndFinish && (
								<SaveAndFinishLater refereeToken={props.refereeToken} currentPagePath={location.pathname} />
							)}
						</>
					);
				}}
			</Formik>
		</form>
	);
};

interface Props {
	refereeToken: string;
	reference: Reference;
	onBackButtonClick: (document: Document) => void;
	onSubmitButtonClick?: () => void;
	customerId?: number;
	showSaveAndFinish?: boolean;
}

ReferenceReplacementDocument.defaultProps = {
	customerId: null,
	showSaveAndFinish: true,
	onSubmitButtonClick: null
};

interface FormikValues {
	childReference: {
		referenceReplacementDocument: Document;
	};
}

export default ReferenceReplacementDocument;
